import React, { useState, useEffect } from "react";

/* ────────────────────────────────────────────
   Pricing data
   ──────────────────────────────────────────── */

interface PlanFeature {
  text: string;
}

interface Plan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: PlanFeature[];
  cta: string;
  ctaLink: string;
  secondaryCta?: string;
  secondaryCtaLink?: string;
  trialNote?: string;
  popular?: boolean;
  overage?: string;
}

interface ProductPricing {
  tab: string;
  slug: string;
  trial?: string;
  plans: Plan[];
}

const pricingData: ProductPricing[] = [
  {
    tab: "CAPRO",
    slug: "capro",
    trial: "Free 7-Day Trial — 30 minutes included, no credit card required.",
    plans: [
      {
        name: "Starter",
        price: "$199",
        period: "/mo",
        description: "Ideal for growing small businesses.",
        features: [
          { text: "400 minutes included" },
          { text: "1 Number included" },
          { text: "24/7 AI receptionist" },
          { text: "AI appointment booking, re-scheduling and cancellation" },
          { text: "Smart spam filtering" },
          { text: "Simultaneous calling" },
          { text: "Call forwarding to staff" },
          { text: "Call recordings" },
          { text: "Analytics" },
          { text: "Call transcription" },
          { text: "Email support" },
          { text: "AI-Powered Sentiment Analysis" },
          { text: "Automated SMS & Email Alerts" },
          { text: "Web Data Scraping (URLs, Documents & Images)" },
          { text: "Seamless CRM & POS Integration" },
          { text: "Fully White-Labeled Application" },
          { text: "English + French support" },
        ],
        cta: "Subscribe Now",
        ctaLink: "https://dashboard.torqai.ca/auth/register?plan=starter&direct=1",
        secondaryCta: "Try free for 7 days →",
        secondaryCtaLink: "https://dashboard.torqai.ca/auth/register?plan=starter",
        trialNote: "Cancel anytime during the trial. No card required.",
        overage: "$0.65/minute",
      },
      {
        name: "Growth",
        price: "$449",
        period: "/mo",
        description: "Scale your call handling capacity.",
        features: [
          { text: "1,000 minutes included" },
          { text: "2 Numbers included" },
          { text: "24/7 AI receptionist" },
          { text: "AI appointment booking, re-scheduling and cancellation" },
          { text: "Smart spam filtering" },
          { text: "Simultaneous calling" },
          { text: "Call forwarding to staff" },
          { text: "Call recordings" },
          { text: "Analytics" },
          { text: "Call transcription" },
          { text: "Priority support" },
          { text: "AI-Powered Sentiment Analysis" },
          { text: "Automated SMS & Email Alerts" },
          { text: "Web Data Scraping (URLs, Documents & Images)" },
          { text: "Seamless CRM & POS Integration" },
          { text: "Fully White-Labeled Application" },
          { text: "30+ language support" },
        ],
        cta: "Subscribe Now",
        ctaLink: "https://dashboard.torqai.ca/auth/register?plan=growth&direct=1",
        secondaryCta: "Try free for 7 days →",
        secondaryCtaLink: "https://dashboard.torqai.ca/auth/register?plan=growth",
        trialNote: "Cancel anytime during the trial. No card required.",
        popular: true,
        overage: "$0.60/minute",
      },
      {
        name: "Professional",
        price: "$899",
        period: "/mo",
        description: "Full-featured AI receptionist solution.",
        features: [
          { text: "2,500 minutes included" },
          { text: "5 Numbers included" },
          { text: "24/7 AI receptionist" },
          { text: "AI appointment booking, re-scheduling and cancellation" },
          { text: "Smart spam filtering" },
          { text: "Simultaneous calling" },
          { text: "Call forwarding to staff" },
          { text: "Call recordings" },
          { text: "Full analytics suite" },
          { text: "Custom voice settings" },
          { text: "Phone support" },
          { text: "AI-Powered Sentiment Analysis" },
          { text: "Automated SMS & Email Alerts" },
          { text: "Web Data Scraping (URLs, Documents & Images)" },
          { text: "Seamless CRM & POS Integration" },
          { text: "Fully White-Labeled Application" },
          { text: "45+ language support" },
        ],
        cta: "Subscribe Now",
        ctaLink: "https://dashboard.torqai.ca/auth/register?plan=pro&direct=1",
        secondaryCta: "Try free for 7 days →",
        secondaryCtaLink: "https://dashboard.torqai.ca/auth/register?plan=pro",
        trialNote: "Cancel anytime during the trial. No card required.",
        overage: "$0.50/minute",
      },
      {
        name: "Enterprise",
        price: "Custom",
        period: "",
        description: "Tailored for large organizations.",
        features: [
          { text: "5,000+ minutes" },
          { text: "Dedicated account manager" },
          { text: "Call recordings" },
          { text: "99.9% SLA guarantee" },
          { text: "AI-Powered Sentiment Analysis" },
          { text: "Automated SMS & Email Alerts" },
          { text: "Web Data Scraping (URLs, Documents & Images)" },
          { text: "Seamless CRM & POS Integration" },
          { text: "Fully White-Labeled Application" },
        ],
        cta: "Contact Sales",
        ctaLink: "/contact",
      },
    ],
  },
  {
    tab: "SAPRO",
    slug: "sapro",
    plans: [
      {
        name: "Starter",
        price: "$250",
        period: "/mo",
        description: "Begin automating outbound sales.",
        features: [
          { text: "400 minutes included" },
          { text: "1,000 emails" },
          { text: "1,000 sms" },
          { text: "Analytics" },
          { text: "Call transcription" },
          { text: "Email support" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
      },
      {
        name: "Growth",
        price: "$549",
        period: "/mo",
        description: "Multiply your sales capacity.",
        features: [
          { text: "1,000 minutes included" },
          { text: "5,000 emails" },
          { text: "5,000 sms" },
          { text: "Analytics" },
          { text: "Priority support" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
        popular: true,
      },
      {
        name: "Professional",
        price: "$999",
        period: "/mo",
        description: "Full-powered AI sales engine.",
        features: [
          { text: "2,500 minutes included" },
          { text: "10,000 emails" },
          { text: "10,000 sms" },
          { text: "Custom voice settings" },
          { text: "Full analytics suite" },
          { text: "Phone support" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
      },
      {
        name: "Enterprise",
        price: "Custom",
        period: "",
        description: "Unlimited outbound at scale.",
        features: [
          { text: "Unlimited minutes" },
          { text: "Unlimited contacts" },
          { text: "Unlimited campaigns" },
          { text: "10+ concurrent calls" },
          { text: "Dedicated CSM + 99.9% SLA" },
        ],
        cta: "Contact Sales",
        ctaLink: "/contact",
      },
    ],
  },
  {
    tab: "VAPRO",
    slug: "vapro",
    plans: [
      {
        name: "Starter",
        price: "$39",
        period: "/mo",
        description: "Start managing your social presence.",
        features: [
          { text: "3 social accounts" },
          { text: "30 posts/month" },
          { text: "50 AI generations" },
          { text: "Analytics" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
      },
      {
        name: "Growth",
        price: "$79",
        period: "/mo",
        description: "Amplify your brand across platforms.",
        features: [
          { text: "6 social accounts" },
          { text: "100 posts/month" },
          { text: "200 AI generations" },
          { text: "Brand voice customization" },
          { text: "Priority support" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
        popular: true,
      },
      {
        name: "Professional",
        price: "$129",
        period: "/mo",
        description: "Complete social media command center.",
        features: [
          { text: "15 social accounts" },
          { text: "300 posts/month" },
          { text: "All 7+ platforms" },
          { text: "1,000 AI generations" },
          { text: "Team collaboration" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
      },
      {
        name: "Enterprise",
        price: "Custom",
        period: "",
        description: "Unlimited scale for large teams.",
        features: [
          { text: "Unlimited accounts" },
          { text: "Unlimited posts" },
          { text: "Unlimited AI generations" },
          { text: "White-label reports" },
        ],
        cta: "Contact Sales",
        ctaLink: "/contact",
      },
    ],
  },
  {
    tab: "HAPRO",
    slug: "hapro",
    plans: [
      {
        name: "Starter",
        price: "$399",
        period: "/mo",
        description: "Start screening smarter.",
        features: [
          { text: "25 interviews/month" },
          { text: "5 templates" },
          { text: "7-day recording retention" },
          { text: "Resume parsing" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
      },
      {
        name: "Growth",
        price: "$699",
        period: "/mo",
        description: "Scale your hiring pipeline.",
        features: [
          { text: "50 interviews/month" },
          { text: "20 templates" },
          { text: "30-day retention" },
          { text: "Sentiment analysis" },
          { text: "PDF exports" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
        popular: true,
      },
      {
        name: "Professional",
        price: "$1,199",
        period: "/mo",
        description: "Enterprise-grade hiring automation.",
        features: [
          { text: "100 interviews/month" },
          { text: "50 templates" },
          { text: "90-day retention" },
          { text: "Custom AI personality" },
          { text: "ATS integration" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
      },
      {
        name: "Enterprise",
        price: "Custom",
        period: "",
        description: "Full-scale recruitment solution.",
        features: [
          { text: "Unlimited interviews" },
          { text: "Unlimited templates" },
          { text: "1-year recording retention" },
          { text: "Dedicated CSM" },
        ],
        cta: "Contact Sales",
        ctaLink: "/contact",
      },
    ],
  },
  {
    tab: "TAPRO",
    slug: "tapro",
    plans: [
      {
        name: "Starter",
        price: "$599",
        period: "/mo",
        description: "Start training with AI roleplay.",
        features: [
          { text: "30 sessions/month" },
          { text: "10 employees" },
          { text: "5 industry scenarios" },
          { text: "Basic scoring" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
      },
      {
        name: "Growth",
        price: "$1,099",
        period: "/mo",
        description: "Level up your team's skills.",
        features: [
          { text: "60 sessions/month" },
          { text: "50 employees" },
          { text: "15 industry scenarios" },
          { text: "Progress analytics" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
        popular: true,
      },
      {
        name: "Professional",
        price: "$1,699",
        period: "/mo",
        description: "Complete training platform.",
        features: [
          { text: "100 sessions/month" },
          { text: "150 employees" },
          { text: "30 scenarios" },
          { text: "Manager dashboard" },
          { text: "Certification tracking" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
      },
      {
        name: "Enterprise",
        price: "Custom",
        period: "",
        description: "Organization-wide training.",
        features: [
          { text: "Unlimited sessions" },
          { text: "Unlimited employees" },
          { text: "SSO & SCIM" },
          { text: "Dedicated onboarding" },
          { text: "Custom content library" },
        ],
        cta: "Contact Sales",
        ctaLink: "/contact",
      },
    ],
  },
  {
    tab: "INPRO",
    slug: "inpro",
    trial: "Free 7-Day Trial, no credit card required.",
    plans: [
      {
        name: "Starter",
        price: "$199",
        period: "/mo",
        description: "Get AI-powered business insights.",
        features: [
          { text: "Weekly AI insights reports" },
          { text: "2 products analyzed" },
          { text: "90-day report history" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
      },
      {
        name: "Growth",
        price: "$299",
        period: "/mo",
        description: "Deeper, more frequent insights.",
        features: [
          { text: "2x/week reports" },
          { text: "All products analyzed" },
          { text: "180-day history" },
          { text: "Custom focus areas" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
        popular: true,
      },
      {
        name: "Professional",
        price: "$399",
        period: "/mo",
        description: "Daily intelligence for decision-makers.",
        features: [
          { text: "Daily reports" },
          { text: "All products analyzed" },
          { text: "1-year history" },
          { text: "Trend alerts" },
        ],
        cta: "Get Started",
        ctaLink: "/contact",
      },
      {
        name: "Enterprise",
        price: "Custom",
        period: "",
        description: "Complete business intelligence suite.",
        features: [
          { text: "On-demand reports" },
          { text: "Unlimited history" },
          { text: "White-label reports" },
          { text: "Dedicated analyst" },
        ],
        cta: "Contact Sales",
        ctaLink: "/contact",
      },
    ],
  },
];


/* ──────────── Circle Checkmark SVG ──────────── */
const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="10" cy="10" r="10" fill="#ba6be7" />
    <path d="M6 10L9 13L14 7.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ──────────── Inline styles ──────────── */

const styles: Record<string, React.CSSProperties> = {
  section: {
    padding: "100px 0 120px",
    position: "relative",
  },

  /* ── Cards ── */
  card: {
    position: "relative",
    padding: "32px 28px 36px",
    borderRadius: 10,
    background: "linear-gradient(209deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%)",
    backdropFilter: "blur(40px)",
    WebkitBackdropFilter: "blur(40px)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.35s, box-shadow 0.35s",
    overflow: "visible",
    boxShadow: "0 4px 24px -1px rgba(28, 9, 61, 0.2)",
  },
  cardPopular: {
    border: "1px solid #ba6be7",
    boxShadow:
      "0 4px 24px -1px rgba(28, 9, 61, 0.2), 0 0 50px rgba(186, 107, 231, 0.08)",
  },

  /* ── Popular Badge ── */
  popularBadge: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translate(-50%, -50%)",
    background: "#ba6be7",
    color: "#0a0a14",
    padding: "6px 18px",
    borderRadius: "50px",
    fontSize: "10px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    boxShadow: "0 4px 20px rgba(186, 107, 231, 0.45)",
    zIndex: 10,
    whiteSpace: "nowrap",
  },

  /* ── Glow orbs ── */
  glowOrb: {
    position: "absolute",
    width: 80,
    height: 80,
    borderRadius: "50%",
    filter: "blur(50px)",
    opacity: 0.25,
    pointerEvents: "none",
    zIndex: 0,
  },



  /* ── Plan info ── */
  planName: {
    fontSize: 22,
    fontWeight: 500,
    color: "#fff",
    marginBottom: 4,
    position: "relative",
    zIndex: 1,
    fontFamily: "var(--font-heading)",
  },
  planDesc: {
    fontSize: 13,
    color: "#6b6c80",
    lineHeight: 1.5,
    marginBottom: 12,
    position: "relative",
    zIndex: 1,
  },

  /* ── Price ── */
  priceWrap: {
    marginBottom: 16,
    position: "relative",
    zIndex: 1,
  },
  priceAmount: {
    fontSize: 44,
    fontWeight: 500,
    color: "#fff",
    letterSpacing: "-0.03em",
    lineHeight: 1,
    fontFamily: "var(--font-heading)",
  },
  pricePeriod: {
    fontSize: 14,
    color: "#6b6c80",
    marginLeft: 8,
    fontWeight: 400,
  },

  /* ── Divider ── */
  divider: {
    height: 1,
    background: "rgba(255,255,255,0.07)",
    margin: "16px 0",
    position: "relative",
    zIndex: 1,
  },

  /* ── Features ── */
  featuresLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: "#fff",
    marginBottom: 12,
    position: "relative",
    zIndex: 1,
  },
  featureList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    flex: 1,
    position: "relative",
    zIndex: 1,
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 10,
    fontSize: 14,
    color: "#a0a1b2",
    lineHeight: 1.5,
  },

  /* ── CTA ── */
  ctaBtn: {
    display: "block",
    textAlign: "center",
    padding: "14px 24px",
    borderRadius: 50,
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    textDecoration: "none",
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.15)",
    transition: "all 0.3s ease",
    position: "relative",
    zIndex: 1,
    letterSpacing: "0.02em",
  },
  ctaBtnPopular: {
    background: "#ba6be7",
    border: "1px solid #ba6be7",
    color: "#0a0a14",
    fontWeight: 700,
    boxShadow: "0 4px 25px rgba(186,107,231,0.35)",
  },
};

/* ──────────── Component ──────────── */

const PricingSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isCalculatorView, setIsCalculatorView] = useState(false);
  const currentProduct = pricingData[activeTab];

  // Glow orb positions for visual variety
  const orbPositions = [
    { top: -20, left: -20, background: "rgba(186,107,231,0.5)" },
    { bottom: -15, right: -15, background: "rgba(44,50,254,0.4)" },
    { top: "40%", right: -25, background: "rgba(0,164,175,0.35)" },
    { bottom: -20, left: "30%", background: "rgba(186,107,231,0.4)" },
  ];

  return (
    <section className="pricing-section-container" style={styles.section}>
      <style>{`
        @media (max-width: 767px) {
          .pricing-glass-card {
            align-items: center !important;
            text-align: center !important;
          }
          .pricing-glass-card .price-amount-text {
            font-weight: 500 !important;
          }
          .pricing-glass-card .feature-list-mobile {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            width: 100%;
            text-align: left;
          }
          .pricing-glass-card .mobile-cta-btn {
            width: 100% !important;
          }
        }
      `}</style>
      <div className="container">
        {/* ===== Header ===== */}
        <div className="sec-title sec-title-center about-sec-title about-sec-title-two mb-60">
          <span className="sub-title mb-10">Our Pricing</span>
          <h2 className="title">
            Simple, Transparent Pricing
          </h2>
          <p className="content" style={{ maxWidth: 600, margin: '20px auto 30px' }}>
            All prices in CAD. Cancel anytime.
          </p>

          {/* View Toggle */}
          <div className="view-toggle-wrapper d-flex justify-content-center mt-40">
            <div className="view-toggle pill-toggle d-flex p-1" style={{ 
              background: 'rgba(255,255,255,0.05)', 
              borderRadius: '50px',
              width: 'max-content'
            }}>
              <button 
                className={`toggle-btn px-4 py-2 ${!isCalculatorView ? 'active' : ''}`}
                onClick={() => setIsCalculatorView(false)}
                style={{
                  border: 'none',
                  borderRadius: '50px',
                  background: !isCalculatorView ? '#ba6be7' : 'transparent',
                  color: !isCalculatorView ? '#0a0a14' : 'rgba(255,255,255,0.6)',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                Standard Plans
              </button>
              <button 
                className={`toggle-btn px-4 py-2 ${isCalculatorView ? 'active' : ''}`}
                onClick={() => setIsCalculatorView(true)}
                style={{
                  border: 'none',
                  borderRadius: '50px',
                  background: isCalculatorView ? '#ba6be7' : 'transparent',
                  color: isCalculatorView ? '#0a0a14' : 'rgba(255,255,255,0.6)',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                Usage Calculator
              </button>
            </div>
          </div>
        </div>

        {isCalculatorView ? (
          <PricingCalculatorInternal />
        ) : (
          <>
            {/* ===== Product Tabs ===== */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 50,
              }}
            >
              {pricingData.map((product, i) => (
                <button
                  key={product.slug}
                  onClick={() => setActiveTab(i)}
                  style={{
                    padding: "12px 28px",
                    border: "1px solid",
                    borderColor:
                      activeTab === i
                        ? "#ba6be7"
                        : "rgba(255,255,255,0.1)",
                    borderRadius: 50,
                    background:
                      activeTab === i
                        ? "linear-gradient(135deg, rgba(186,107,231,0.18), rgba(44,50,254,0.1))"
                        : "rgba(255,255,255,0.03)",
                    color: activeTab === i ? "#fff" : "#6b6c80",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    letterSpacing: "0.04em",
                  }}
                >
                  {product.tab}
                </button>
              ))}
            </div>

            {/* ===== Trial Banner ===== */}
            {currentProduct.trial && (
              <div
                style={{
                  textAlign: "center",
                  padding: "14px 30px",
                  background: "rgba(186,107,231,0.06)",
                  borderRadius: 14,
                  border: "1px solid rgba(186,107,231,0.18)",
                  maxWidth: 520,
                  margin: "0 auto 50px",
                  backdropFilter: "blur(12px)",
                }}
              >
                <span
                  style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}
                >
                  {currentProduct.trial}
                </span>
              </div>
            )}

            {/* ===== Plan Cards ===== */}
            <div className="row mt-none-30">
              {currentProduct.plans.map((plan, idx) => (
                <div className="col-xl-3 col-lg-6 col-md-6 mt-30 d-flex" key={idx}>
                  <div
                    className="xb-border pricing-glass-card w-100"
                    style={{
                      ...styles.card,
                      ...(plan.popular ? styles.cardPopular : {}),
                    }}
                    onMouseEnter={(e) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.transform = "translateY(-6px)";
                      el.style.boxShadow =
                        "0 8px 50px rgba(186,107,231,0.15), 0 4px 24px -1px rgba(28, 9, 61, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.transform = "translateY(0)";
                      el.style.boxShadow = plan.popular
                        ? "0 4px 24px -1px rgba(28, 9, 61, 0.2), 0 0 50px rgba(186, 107, 231, 0.08)"
                        : "0 4px 24px -1px rgba(28, 9, 61, 0.2)";
                    }}
                  >
                    {plan.popular && (
                      <div style={styles.popularBadge}>
                        <i className="fas fa-star" style={{ fontSize: "9px" }}></i>
                        MOST POPULAR
                      </div>
                    )}

                    {/* Glow orb */}
                    <div
                      style={{
                        ...styles.glowOrb,
                        ...orbPositions[idx % 4],
                      }}
                    />

                    {/* Plan name */}
                    {/* Top alignment wrapper to keep secondary buttons aligned */}
                    <div style={{ minHeight: "185px", display: "flex", flexDirection: "column" }}>
                      {plan.price !== "Custom" ? (
                        <>
                          {/* Plan name */}
                          <h4 style={{ ...styles.planName, textAlign: "center", marginBottom: "8px" }}>{plan.name}</h4>

                          {/* Price */}
                          <div style={{ ...styles.priceWrap, textAlign: "center", marginBottom: "12px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "baseline" }}>
                              <span className="price-amount-text" style={styles.priceAmount}>
                                {plan.price}
                              </span>
                              <span style={{ fontSize: "14px", fontWeight: 500, marginLeft: "6px", color: "#fff" }}>CAD</span>
                            </div>
                            <div style={{ ...styles.pricePeriod, marginLeft: 0, marginTop: "4px" }}>per month</div>
                          </div>
                          
                          {/* Stats (Primary Feature & Overage) / Description */}
                          <div style={{ minHeight: "45px", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                               {/* Left side: Primary Feature */}
                               {plan.features.length > 0 && (
                                   <div style={{ textAlign: "center", flex: 1 }}>
                                     <div style={{ fontSize: "16px", fontWeight: "bold", color: "#fff", lineHeight: 1 }}>{plan.features[0].text.split(" ")[0]}</div>
                                     <div style={{ fontSize: "11px", color: "#6b6c80", marginTop: "4px", whiteSpace: "nowrap" }}>{plan.features[0].text.split(" ").slice(1).join(" ")}</div>
                                   </div>
                               )}
                               {/* Right side: overage */}
                               {plan.overage && (
                                   <div style={{ textAlign: "center", flex: 1, borderLeft: "1px solid rgba(255,255,255,0.1)" }}>
                                     <div style={{ fontSize: "16px", fontWeight: "bold", color: "#fff", lineHeight: 1 }}>{plan.overage.split("/")[0]}</div>
                                     <div style={{ fontSize: "11px", color: "#6b6c80", marginTop: "4px", whiteSpace: "nowrap" }}>per extra min</div>
                                   </div>
                               )}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, paddingBottom: "16px" }}>
                           <h4 style={{ ...styles.planName, textAlign: "center", marginBottom: "4px" }}>{plan.name}</h4>
                           <span className="price-amount-text" style={{ ...styles.priceAmount, fontSize: 32, textAlign: "center", marginBottom: "12px" }}>
                             Custom
                           </span>
                           <div style={{ textAlign: "center", fontSize: "13px", color: "#6b6c80", lineHeight: "1.5" }}>
                             {plan.description}
                           </div>
                        </div>
                      )}
                    </div>

                    {/* Trial button / Custom Solutions */}
                    {(currentProduct.tab === "CAPRO" || currentProduct.tab === "INPRO") && (
                      <div style={{ textAlign: "center", marginBottom: "12px", marginTop: "12px" }}>
                        {plan.price === "Custom" ? (
                          <a href={plan.secondaryCtaLink || plan.ctaLink || "/contact"} style={{ 
                              display: "inline-block", 
                              background: "rgba(186, 107, 231, 0.08)", 
                              color: "#ba6be7", 
                              padding: "8px 24px", 
                              borderRadius: "6px", 
                              fontSize: "13px", 
                              fontWeight: "bold",
                              textDecoration: "none",
                              transition: "background 0.3s ease"
                          }}
                          onMouseEnter={(e) => {
                              (e.currentTarget as HTMLElement).style.background = "rgba(186, 107, 231, 0.15)";
                          }}
                          onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.background = "rgba(186, 107, 231, 0.08)";
                          }}>
                              Custom Solutions
                          </a>
                        ) : (
                          <a href={plan.secondaryCtaLink || plan.ctaLink || "/contact"} style={{ 
                              display: "inline-block", 
                              background: "rgba(186, 107, 231, 0.08)", 
                              color: "#ba6be7", 
                              padding: "8px 24px", 
                              borderRadius: "6px", 
                              fontSize: "13px", 
                              fontWeight: "bold",
                              textDecoration: "none",
                              transition: "background 0.3s ease"
                          }}
                          onMouseEnter={(e) => {
                              (e.currentTarget as HTMLElement).style.background = "rgba(186, 107, 231, 0.15)";
                          }}
                          onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.background = "rgba(186, 107, 231, 0.08)";
                          }}>
                              First 7 days FREE
                          </a>
                        )}
                      </div>
                    )}

                    {/* Divider */}
                    <div style={{ ...styles.divider, margin: "12px 0" }} />

                    {/* Features list */}
                    <ul className="feature-list-mobile" style={{ ...styles.featureList, flexGrow: 1, marginBottom: "20px", marginTop: "16px" }}>
                      {plan.features.map((f, fi) => {
                         // Skip rendering the first feature since it's pulled out for non-custom plans
                         if (plan.price !== "Custom" && fi === 0) return null;
                         return (
                            <li key={fi} style={styles.featureItem}>
                              <CheckIcon />
                              {f.text}
                            </li>
                         );
                      })}
                    </ul>

                    {/* Main CTA Button */}
                    <a
                      href={plan.ctaLink}
                      className="mobile-cta-btn"
                      style={{
                        ...styles.ctaBtn,
                        ...(plan.popular ? styles.ctaBtnPopular : {}),
                        marginTop: "auto"
                      }}
                      onMouseEnter={(e) => {
                        const el = e.currentTarget as HTMLElement;
                        if (!plan.popular) {
                          el.style.background = "#ba6be7";
                          el.style.borderColor = "#ba6be7";
                          el.style.color = "#0a0a14";
                          el.style.boxShadow = "0 4px 25px rgba(186,107,231,0.35)";
                        } else {
                          el.style.background = "#c980ed";
                          el.style.boxShadow = "0 6px 30px rgba(186,107,231,0.45)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        const el = e.currentTarget as HTMLElement;
                        if (!plan.popular) {
                          el.style.background = "transparent";
                          el.style.borderColor = "rgba(255,255,255,0.15)";
                          el.style.color = "#fff";
                          el.style.boxShadow = "none";
                        } else {
                          el.style.background = "#ba6be7";
                          el.style.boxShadow = "0 4px 25px rgba(186,107,231,0.35)";
                        }
                      }}
                    >
                      {plan.cta}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────
   Internal Pricing Calculator Component
   ──────────────────────────────────────────── */

interface CalcPlan {
    name: string;
    price: number;
    limits: Record<string, number | string>;
}

interface CalcProductData {
    id: string;
    name: string;
    icon: string;
    metrics: {
        id: string;
        label: string;
        min: number;
        max: number;
        step: number;
        defaultValue: number;
        unit?: string;
    }[];
    plans: CalcPlan[];
    overage?: {
        metric: string;
        rate: number;
        unit: string;
    };
}

const calcProducts: CalcProductData[] = [
    {
        id: 'capro',
        name: 'CAPRO (Voice)',
        icon: 'flaticon-voice-control',
        metrics: [
            { id: 'minutes', label: 'Expected call minutes per month', min: 100, max: 10000, step: 100, defaultValue: 800 },
            { id: 'phone_numbers', label: 'Phone numbers needed', min: 1, max: 10, step: 1, defaultValue: 1 },
            { id: 'offerings', label: 'Your offerings', min: 10, max: 1000, step: 10, defaultValue: 50 },
        ],
        plans: [
            { name: 'Starter', price: 199, limits: { minutes: 400, phone_numbers: 1, offerings: 50 } },
            { name: 'Growth', price: 449, limits: { minutes: 1000, phone_numbers: 2, offerings: 200 } },
            { name: 'Professional', price: 899, limits: { minutes: 2500, phone_numbers: 5, offerings: 1000 } },
        ],
        overage: { metric: 'minutes', rate: 0.35, unit: 'minute' }
    },
    {
        id: 'sapro',
        name: 'SAPRO (Sales)',
        icon: 'flaticon-customer-service',
        metrics: [
            { id: 'minutes', label: 'Call minutes per month', min: 100, max: 10000, step: 100, defaultValue: 400 },
            { id: 'emails', label: 'Monthly emails', min: 100, max: 20000, step: 100, defaultValue: 1000 },
            { id: 'sms', label: 'Monthly SMS', min: 100, max: 20000, step: 100, defaultValue: 1000 },
        ],
        plans: [
            { name: 'Starter', price: 250, limits: { minutes: 400, emails: 1000, sms: 1000, phone_numbers: 1 } },
            { name: 'Growth', price: 549, limits: { minutes: 1000, emails: 5000, sms: 5000, phone_numbers: 2 } },
            { name: 'Professional', price: 999, limits: { minutes: 2500, emails: 10000, sms: 10000, phone_numbers: 5 } },
        ],
    },
    {
        id: 'vapro',
        name: 'VAPRO (Social)',
        icon: 'flaticon-social-media',
        metrics: [
            { id: 'accounts', label: 'Social accounts managed', min: 1, max: 50, step: 1, defaultValue: 5 },
            { id: 'posts', label: 'Monthly posts generated', min: 10, max: 500, step: 10, defaultValue: 30 },
        ],
        plans: [
            { name: 'Starter', price: 199, limits: { accounts: 5, posts: 50 } },
            { name: 'Growth', price: 449, limits: { accounts: 10, posts: 200 } },
            { name: 'Professional', price: 899, limits: { accounts: 15, posts: 500 } },
        ]
    },
    {
        id: 'hapro',
        name: 'HAPRO (Hiring)',
        icon: 'flaticon-recruitment',
        metrics: [
            { id: 'interviews', label: 'AI interviews per month', min: 10, max: 1000, step: 10, defaultValue: 50 },
            { id: 'seats', label: 'Recruiter seats', min: 1, max: 20, step: 1, defaultValue: 1 },
        ],
        plans: [
            { name: 'Starter', price: 299, limits: { interviews: 50, seats: 2 } },
            { name: 'Growth', price: 649, limits: { interviews: 150, seats: 5 } },
            { name: 'Professional', price: 1299, limits: { interviews: 300, seats: 10 } },
        ]
    },
    {
        id: 'tapro',
        name: 'TAPRO (Training)',
        icon: 'flaticon-training',
        metrics: [
            { id: 'employees', label: 'Staff members', min: 5, max: 500, step: 5, defaultValue: 25 },
            { id: 'courses', label: 'Active training courses', min: 1, max: 50, step: 1, defaultValue: 3 },
        ],
        plans: [
            { name: 'Starter', price: 199, limits: { employees: 25, courses: 5 } },
            { name: 'Growth', price: 449, limits: { employees: 75, courses: 15 } },
            { name: 'Professional', price: 899, limits: { employees: 150, courses: 30 } },
        ]
    },
    {
        id: 'inpro',
        name: 'INPRO (Insights)',
        icon: 'flaticon-data-analytics',
        metrics: [
            { id: 'reports', label: 'Monthly BI reports', min: 1, max: 100, step: 1, defaultValue: 5 },
            { id: 'data_sources', label: 'Integrated data sources', min: 1, max: 20, step: 1, defaultValue: 2 },
        ],
        plans: [
            { name: 'Starter', price: 199, limits: { reports: 5, data_sources: 2 } },
            { name: 'Growth', price: 449, limits: { reports: 15, data_sources: 5 } },
            { name: 'Professional', price: 899, limits: { reports: 30, data_sources: 10 } },
        ]
    }
];

const PricingCalculatorInternal: React.FC = () => {
    const [activeProduct, setActiveProduct] = useState(calcProducts[0]);
    const [usage, setUsage] = useState<Record<string, number>>(() => {
        const initialUsage: Record<string, number> = {};
        calcProducts[0].metrics.forEach(m => initialUsage[m.id] = m.defaultValue);
        return initialUsage;
    });

    const handleProductSwitch = (product: CalcProductData) => {
        setActiveProduct(product);
        const newUsage: Record<string, number> = {};
        product.metrics.forEach(m => newUsage[m.id] = m.defaultValue);
        setUsage(newUsage);
    };

    const handleSliderChange = (id: string, value: number) => {
        setUsage(prev => ({ ...prev, [id]: value }));
    };

    const calculateRecommendedPlan = () => {
        let recommendedPlan = activeProduct.plans[activeProduct.plans.length - 1];

        for (const plan of activeProduct.plans) {
            const fitsAllLimits = Object.entries(usage).every(([metricId, value]) => {
                const limit = plan.limits[metricId];
                return typeof limit === 'number' && value <= limit;
            });

            if (fitsAllLimits) {
                recommendedPlan = plan;
                break;
            }
        }

        let totalMonthly = recommendedPlan.price;
        let overageDetails: { metric: string; amount: number; rate: number; total: number } | null = null;

        if (activeProduct.overage) {
            const overageMetric = activeProduct.overage.metric;
            const limit = recommendedPlan.limits[overageMetric] as number;
            const currentUsage = usage[overageMetric];

            if (currentUsage > limit) {
                const overageAmount = currentUsage - limit;
                const overageCost = overageAmount * activeProduct.overage.rate;
                totalMonthly += overageCost;
                overageDetails = {
                    metric: overageMetric,
                    amount: overageAmount,
                    rate: activeProduct.overage.rate,
                    total: overageCost
                };
            }
        }

        return { plan: recommendedPlan, total: totalMonthly, overage: overageDetails };
    };

    const { plan, total, overage } = calculateRecommendedPlan();

    return (
        <div className="pricing-calculator-wrapper">
            <div className="product-tabs mb-50">
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <ul className="calc-tabs justify-content-center">
                            {calcProducts.map((product) => (
                                <li key={product.id} className="calc-tab-item">
                                    <button
                                        type="button"
                                        className={`calc-tab-link ${activeProduct.id === product.id ? 'active' : ''}`}
                                        onClick={() => handleProductSwitch(product)}
                                    >
                                        {product.id.toUpperCase()}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            <div className="row g-4 align-items-stretch">
                <div className="col-lg-7 d-flex">
                    <div className="usage-card xb-blog-item xb-border w-100">
                        <div className="xb-item--content">
                            <h3 className="xb-item--title mb-40 text-white">Your usage</h3>
                            <p className="mb-40" style={{ opacity: 0.7 }}>
                                Adjust the sliders to match your expected usage. We'll recommend the best plan.
                            </p>

                            <div className="metrics-list">
                                {activeProduct.metrics.map((metric) => (
                                    <div key={metric.id} className="metric-item mb-40">
                                        <div className="d-flex justify-content-between align-items-center mb-15">
                                            <label className="text-white fw-bold">{metric.label}</label>
                                            <span className="metric-value fw-bold" style={{ fontSize: '1.2rem', color: '#c484f4' }}>
                                                {(usage[metric.id] ?? metric.defaultValue).toLocaleString()}
                                            </span>
                                        </div>
                                        <input
                                            type="range"
                                            className="form-range custom-slider"
                                            min={metric.min}
                                            max={metric.max}
                                            step={metric.step}
                                            value={usage[metric.id] ?? metric.defaultValue}
                                            onInput={(e) => handleSliderChange(metric.id, parseInt((e.target as HTMLInputElement).value))}
                                        />
                                        <div className="d-flex justify-content-between mt-10" style={{ fontSize: '0.8rem', opacity: 0.5 }}>
                                            <span>{metric.min.toLocaleString()}</span>
                                            <span>{metric.max.toLocaleString()}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-lg-5 d-flex">
                    <div className="recommendation-card xb-blog-item xb-border w-100">
                        <div className="xb-item--content h-100 d-flex flex-column">
                            <span className="sub-title mb-10" style={{ color: '#c484f4' }}>RECOMMENDED PLAN</span>
                            <h3 className="xb-item--title mb-20 text-white">{plan.name}</h3>

                            <div className="pricing-display mb-30">
                                <span className="currency h2 text-white fw-bold">$</span>
                                <span className="amount h1 text-white fw-bold" style={{ fontSize: '3.5rem' }}>
                                    {Math.round(total).toLocaleString()}
                                </span>
                                <span className="period ms-2" style={{ opacity: 0.7 }}>/mo CAD</span>
                            </div>

                            <div className="breakdown-list mb-40 flex-grow-1">
                                <div className="d-flex justify-content-between py-2 border-bottom border-light border-opacity-10">
                                    <span style={{ opacity: 0.7 }}>Base plan</span>
                                    <span className="text-white">${plan.price}</span>
                                </div>
                                {overage && (
                                    <div className="d-flex justify-content-between py-2 border-bottom border-light border-opacity-10 text-primary" style={{ color: '#c484f4' }}>
                                        <span>Overage: {overage.amount} × ${overage.rate}/{overage.metric}</span>
                                        <span className="fw-bold">${Math.round(overage.total)}</span>
                                    </div>
                                )}

                                <div className="fits-usage-section mt-40">
                                    <label className="sub-title mb-20 d-block" style={{ fontSize: '0.7rem', opacity: 0.5 }}>FITS YOUR USAGE</label>
                                    {activeProduct.metrics.map(metric => {
                                        const limit = plan.limits[metric.id];
                                        const current = usage[metric.id];
                                        const percentage = typeof limit === 'number' ? Math.min((current / limit) * 100, 100) : 100;
                                        const isOver = typeof limit === 'number' && current > limit;

                                        return (
                                            <div key={metric.id} className="progress-item mb-20">
                                                <div className="d-flex justify-content-between mb-10" style={{ fontSize: '0.85rem' }}>
                                                    <span style={{ opacity: 0.8 }}>{metric.label}</span>
                                                    <span className={isOver ? 'text-danger fw-bold' : 'text-white'}>
                                                        {(usage[metric.id] ?? metric.defaultValue).toLocaleString()} / {typeof limit === 'number' ? limit.toLocaleString() : limit}
                                                    </span>
                                                </div>
                                                <div className="progress" style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.05)', overflow: 'visible' }}>
                                                    <div
                                                        className="progress-bar"
                                                        style={{ 
                                                            width: `${percentage}%`,
                                                            backgroundColor: isOver ? '#ff4d4d' : '#c484f4',
                                                            boxShadow: isOver ? '0 0 10px #ff4d4d' : '0 0 10px #c484f4',
                                                            borderRadius: '10px',
                                                            transition: 'width 0.3s ease'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <a 
                                href="/contact" 
                                className="mobile-cta-btn" 
                                style={{ ...styles.ctaBtn, ...styles.ctaBtnPopular, borderRadius: "100px", marginTop: "0" }}
                                onMouseEnter={(e) => {
                                    const el = e.currentTarget as HTMLElement;
                                    el.style.background = "#c980ed";
                                    el.style.boxShadow = "0 6px 30px rgba(186,107,231,0.45)";
                                }}
                                onMouseLeave={(e) => {
                                    const el = e.currentTarget as HTMLElement;
                                    el.style.background = "#ba6be7";
                                    el.style.boxShadow = "0 4px 25px rgba(186,107,231,0.35)";
                                }}
                            >
                                Get Started
                            </a>

                            <p className="text-center mt-20 mb-0" style={{ fontSize: '0.75rem', opacity: 0.5, fontStyle: 'italic' }}>
                                Estimate only. Final pricing confirmed at checkout.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .pricing-calculator-wrapper {
                    padding: 20px 0;
                }
                .product-tabs .calc-tabs {
                    display: flex;
                    flex-wrap: wrap;
                    list-style: none;
                    padding: 0;
                    margin-bottom: 40px;
                    border: none;
                    gap: 15px;
                }
                .product-tabs .calc-tab-link {
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: #6b6c80;
                    background: rgba(255, 255, 255, 0.03);
                    padding: 12px 28px;
                    border-radius: 50px;
                    transition: all 0.3s ease;
                    font-weight: 600;
                    font-size: 14px;
                    letter-spacing: 0.04em;
                    cursor: pointer;
                }
                .product-tabs .calc-tab-link:hover {
                    border-color: rgba(186, 107, 231, 0.5);
                    color: #fff;
                }
                .product-tabs .calc-tab-link.active {
                    border-color: #ba6be7;
                    background: linear-gradient(135deg, rgba(186, 107, 231, 0.18), rgba(44, 50, 254, 0.1));
                    color: #fff;
                }
                .usage-card, .recommendation-card {
                    background: rgba(255, 255, 255, 0.02);
                    backdrop-filter: blur(10px);
                    padding: 40px;
                }
                .custom-slider {
                    -webkit-appearance: none;
                    width: 100%;
                    height: 6px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 5px;
                    outline: none;
                }
                .custom-slider::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 20px;
                    height: 20px;
                    background: #c484f4;
                    cursor: pointer;
                    border-radius: 50%;
                    box-shadow: 0 0 10px rgba(196, 132, 244, 0.5);
                }
                .custom-slider::-moz-range-thumb {
                    width: 20px;
                    height: 20px;
                    background: #c484f4;
                    cursor: pointer;
                    border-radius: 50%;
                    border: none;
                    box-shadow: 0 0 10px rgba(196, 132, 244, 0.5);
                }
                .xb-border {
                    border-radius: 30px;
                }
                .progress {
                    background: rgba(255, 255, 255, 0.05) !important;
                }
            `}</style>
        </div>
    );
};

export default PricingSection;
